import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { resendVerificationEmail } from '../../../actions/auth';

/**
 * The "send it again" control, with a live countdown.
 *
 * WHY THE COUNTDOWN IS RENDERED AT ALL. A resend button that silently does
 * nothing for a minute is indistinguishable from a broken site, and this is a
 * screen a customer only reaches when something has already not gone to plan.
 * So the button says how long is left, in words, and re-enables itself.
 *
 * WHY THE LIMIT IS ALSO ENFORCED ON THE SERVER. Everything here is advisory: a
 * page reload resets it. `POST /v3/auth/verification/resend-slot` owns the real
 * ladder (60 s, doubling, five-minute ceiling, five an hour) and the thunk asks
 * it before sending. Root CLAUDE.md rule 7 — never let a client-supplied
 * control be the only control.
 *
 * @property {Function} props.onSent - Optional; called after a successful send.
 * @property {string} props.className - Optional; extra classes for the button.
 */
class ResendVerificationButton extends Component {

    constructor(props) {
        super(props);
        this.state = { secondsLeft: 0, sent: false };
        this.tick = this.tick.bind(this);
        this.onResend = this.onResend.bind(this);
    }

    componentWillUnmount() {
        if (this.timer) clearInterval(this.timer);
    }

    /**
     * Decrements the countdown and stops the interval when it reaches zero.
     * @returns {void}
     */
    tick() {
        this.setState((prev) => {
            const secondsLeft = Math.max(0, prev.secondsLeft - 1);
            if (secondsLeft === 0 && this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }
            return { secondsLeft };
        });
    }

    /**
     * Starts a fresh countdown, replacing any interval already running.
     * @param {number} seconds - Cooldown length.
     * @returns {void}
     */
    startCountdown(seconds) {
        if (this.timer) clearInterval(this.timer);
        this.setState({ secondsLeft: seconds });
        if (seconds > 0) this.timer = setInterval(this.tick, 1000);
    }

    /**
     * Asks the backend for a slot, sends through Firebase, then starts the
     * cooldown the backend told us to use — for a refusal as well as a success,
     * so the customer is never invited to try again into a closed door.
     * @returns {Promise<void>}
     */
    async onResend() {
        const { sent, cooldownSeconds } = await this.props.resendVerificationEmail();
        this.setState({ sent });
        this.startCountdown(cooldownSeconds || 60);
        if (sent && this.props.onSent) this.props.onSent();
    }

    render() {
        const { translate, resendPending, resendErrorCode, className = '' } = this.props;
        const { secondsLeft, sent } = this.state;
        const waiting = secondsLeft > 0;

        let label = translate('EMAIL_VERIFICATION.RESEND');
        if (resendPending) label = translate('EMAIL_VERIFICATION.RESEND_SENDING');
        else if (waiting) label = translate('EMAIL_VERIFICATION.RESEND_IN', { seconds: secondsLeft });

        return (
            <div className="email-verification-resend">
                <button
                    type="button"
                    className={`btn btn-outline-dark ${className}`}
                    disabled={resendPending || waiting}
                    onClick={this.onResend}
                >
                    {label}
                </button>
                {sent && !resendErrorCode && (
                    <p className="email-verification-resend__note">{translate('EMAIL_VERIFICATION.RESEND_DONE')}</p>
                )}
                {resendErrorCode === 'VERIFICATION_RESEND_THROTTLED' && (
                    <p className="email-verification-resend__note">{translate('EMAIL_VERIFICATION.RESEND_EXHAUSTED')}</p>
                )}
                {resendErrorCode && resendErrorCode !== 'VERIFICATION_RESEND_THROTTLED' && (
                    <p className="email-verification-resend__note">
                        {translate(`ERROR.AUTH.${resendErrorCode}`, null, { showMissingTranslationMsg: false })
                            || translate('ERROR.CODE_UNAVAILABLE')}
                    </p>
                )}
            </div>
        );
    }
}

/**
 * @param {object} state - Redux state.
 * @returns {object} Resend flags plus the translator.
 */
function mapStateToProps(state) {
    return {
        translate: getTranslate(state.locale),
        resendPending: state.auth.verification.resendPending,
        resendErrorCode: state.auth.verification.resendErrorCode,
    };
}

export default connect(mapStateToProps, { resendVerificationEmail })(ResendVerificationButton);
